import React, { useEffect, useState } from 'react';
import { Alert, Anchor, Button, Select, Stack, Text, Title } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
    getDeviceV2Config,
    listPlaylists,
    updateDeviceV2Config,
} from '../../data/ShellConfig';
import { useCsrf } from '../../hooks/useCsrf';

const DeviceV2ConfigForm: React.FC = () => {
    const { deviceId } = useParams<{ deviceId: string }>();
    const { csrfToken } = useCsrf();
    const { data: playlists = [] } = useSWR('api/playlists', listPlaylists);
    const { data: config, mutate } = useSWR(
        deviceId ? `api/device/${deviceId}/v2config` : null,
        () => getDeviceV2Config(deviceId!)
    );
    const [playlistId, setPlaylistId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (config) {
            setPlaylistId(config.playlistId);
        }
    }, [config]);

    const handleSave = async () => {
        if (!deviceId) return;
        setSaving(true);
        setError('');
        setSaved(false);
        try {
            const next = await updateDeviceV2Config(deviceId, playlistId, csrfToken);
            await mutate(next, false);
            setSaved(true);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack gap="md" pt="md">
            <Anchor component={Link} to={`/device/${deviceId}`}>
                Back to device
            </Anchor>
            <Title order={2}>Device Shell Config</Title>
            <Text size="sm" c="dimmed">
                Assign a Playlist for the cloud App Shell. Legacy config remains at{' '}
                <Anchor component={Link} to={`/device/${deviceId}/config`}>
                    /config
                </Anchor>
                .
            </Text>

            <Select
                label="Playlist"
                placeholder="Use default playlist"
                clearable
                data={playlists.map(p => ({ value: p.id, label: p.name }))}
                value={playlistId}
                onChange={setPlaylistId}
                searchable
            />

            {error && (
                <Alert color="red" icon={<IconExclamationCircle />} title="Error">
                    {error}
                </Alert>
            )}
            {saved && (
                <Alert color="green" title="Saved">
                    Playlist assignment saved. Connected App Shells will receive PlaylistConfigUpdate.
                </Alert>
            )}

            <Button onClick={handleSave} loading={saving} style={{ alignSelf: 'flex-start' }}>
                Save
            </Button>
        </Stack>
    );
};

export default DeviceV2ConfigForm;
